import { Platform } from "@angular/cdk/platform";
import { DOCUMENT, Location } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
// import { TranslateService } from '@ngx-translate/core';
import * as Sentry from "@sentry/angular";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FuseConfigService } from "@fuse/services/config.service";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";

import { ActivatedRoute, Router } from "@angular/router";
import { SwUpdate } from "@angular/service-worker";
import { locale as navigationEnglish } from "app/navigation/i18n/en";
import { locale as navigationTurkish } from "app/navigation/i18n/tr";
import { navigation } from "app/navigation/navigation";
import { IdleTimeoutService } from "app/base-module/base-service/idle-timeout.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        // private _translateService: TranslateService,
        private _platform: Platform,
        private swUpdate: SwUpdate,
        private _routeActivated: ActivatedRoute,
        private _route: Router,
        private location: Location,
        private trace: Sentry.TraceService, // Injetar TraceService do Sentry
        private idleTimeout: IdleTimeoutService // PROSP-594: logout por inatividade (4h)
    ) {
        if (location.path() == "") {
            this._route.navigate(["/home"]);
        }

        // Get default navigation
        this.navigation = navigation;

        // Register the navigation to the service
        this._fuseNavigationService.register("main", this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation("main");

        // Add languages
        // this._translateService.addLangs(['en', 'tr']);

        // Set the default language
        // this._translateService.setDefaultLang('en');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(
            navigationEnglish,
            navigationTurkish
        );

        // Use a language
        // this._translateService.use('en');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add("is-mobile");
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.requestNotificationPermission();

        // PROSP-594: monitora inatividade e desloga apos 4h sem interacao.
        this.idleTimeout.start();

        if (this.swUpdate.isEnabled) {
            // todo: verificar
            // this.swUpdate.available.subscribe(() => {
            //     if (confirm("Uma nova versão do sistema está disponível. Deseja atualizar agora?")) {
            //         window.location.reload();
            //     }
            // });
        }
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: any) => {
                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === "boxed") {
                    this.document.body.classList.add("boxed");
                } else {
                    this.document.body.classList.remove("boxed");
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith("theme-")) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });
    }

    /**
     * Solicita permissão de notificação
     * @returns void
     */
    private requestNotificationPermission(): void {
        // Verificar se a API está disponível
        if (!("Notification" in window)) {
            console.warn("Este navegador não suporta notificações");
            return;
        }

        // Verificar se estamos em um contexto seguro
        if (!window.isSecureContext) {
            console.warn(
                "Notificações só funcionam em contextos seguros (HTTPS)"
            );
            return;
        }

        // Verificar permissão atual
        if (Notification.permission === "default") {
            // Usar a API moderna que retorna Promise
            if (typeof Notification.requestPermission === "function") {
                Notification.requestPermission()
                    .then((permission) => {
                        console.log("Permissão de notificação:", permission);
                    })
                    .catch((error) => {
                        console.error(
                            "Erro ao solicitar permissão de notificação:",
                            error
                        );
                    });
            } else {
                console.warn(
                    "Notification.requestPermission não está disponível"
                );
            }
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key: any): void {
        this._fuseSidebarService.getSidebar(key)?.toggleOpen();
    }
}
